<script setup lang="ts">
import { defineAsyncComponent, ref, onMounted, onBeforeUnmount } from 'vue';

const Default = defineAsyncComponent(() => import('@/views/layouts/Default.vue'));

const VITE_ASSET_URL = import.meta.env.VITE_ASSET_URL as string;
const img = (file: string) => `${VITE_ASSET_URL.replace(/\/$/, '')}/assets/public/assets/img/pennylane/${file}`;


// === Données de la démo (7 étapes + fin) ===
type Tap = { x: number; y: number; label?: string; goNext?: boolean };

type Step = {
    id: string; title: string; meta?: string; text: string; img: string; alt: string;
    taps?: Tap[]; // <-- nouveaux hotspots
};
const steps = ref<Step[]>([
    {
        id: 'step-00', title: 'Connectez-vous à Topela (compte Pro)', meta: 'Étape 1/8',
        text: 'Ouvrez l’app Topela et connectez-vous à votre compte professionnel.',
        img: img('1.svg'), alt: 'Écran d\'accueil Topela',
    },
    {
        id: 'step-01', title: 'Ouvrez le menu latéral', meta: 'Étape 1/8',
        text: 'Appuyez sur l’icône menu en haut à gauche.',
        img: img('1.svg'), alt: 'Menu latéral Topela',
        taps: [{ x: 30, y: 10, label: 'Ici', goNext: true }]
    },

    {
        id: 'step-02', title: 'Accédez à votre profil', meta: 'Étape 2/7',
        text: 'Depuis le menu, ouvrez votre profil pour gérer les réglages.',
        img: img('2.svg'), alt: 'Page profil Topela',
        taps: [{ x: 70, y: 30, label: 'Ici', goNext: true }]
    },

    {
        id: 'step-03', title: 'Ouvrez Facturation électronique', meta: 'Étape 3/7',
        text: 'Accédez à la section dédiée pour activer le connecteur Pennylane.',
        img: img('3.svg'), alt: 'Facturation électronique',
        taps: [{ x: 87, y: 73, label: 'Ici', goNext: true }]
    },

    {
        id: 'step-04', title: 'Choisir Pennylane comme Plateforme Agréée', meta: 'Étape 4/7',
        text: 'Appuyez sur « Activer le connecteur » pour sélectionner Pennylane comme plateforme agréée et lancer la connexion.',
        img: img('4.svg'), alt: 'Activer le connecteur',
        taps: [{ x: 87, y: 73, label: 'Ici', goNext: true }]
    },

    {
        id: 'step-05', title: 'Connectez votre compte Pennylane', meta: 'Étape 5/7',
        text: 'Identifiez-vous sur votre compte pennylane.',
        img: img('5.svg'), alt: 'Connexion pennylane',
        taps: [{ x: 70, y: 56, label: 'Ici', goNext: true }]
    },

    {
        id: 'step-06', title: 'Autorisez la connexion', meta: 'Étape 6/7',
        text: 'Validez les autorisations nécessaires à la transmission des factures (OAuth sécurisé).',
        img: img('6.svg'), alt: 'Autorisation connecteur Pennylane',
        taps: [{ x: 87, y: 73, label: 'Ici', goNext: true }]
    },

    {
        id: 'step-07', title: 'Connexion établie ✅', meta: 'Étape 7/7',
        text: 'La transmission automatique vers Pennylane est activée.',
        img: img('7.svg'), alt: 'Connecteur actif'
    }
]);

// Slide de fin (hors TOC)
const finale = {
    id: 'done',
    title: 'C’est tout bon ✅',
    text: 'Chaque facture envoyée à un client passe automatiquement par le PA Pennylane. Vous pouvez voir les factures transmises ou désactiver le connecteur à tout moment.',
    bullets: ['Voir mes factures transmises', 'Déconnecter le connecteur'],
};

const current = ref(steps.value[0].id);




// Lightbox
const lightboxOpen = ref(false);
const lightboxSrc = ref<string | null>(null);
const openLightbox = (src: string) => { lightboxSrc.value = src; lightboxOpen.value = true; };
const closeLightbox = () => { lightboxOpen.value = false; lightboxSrc.value = null; };

// Clavier (Escape)
const onKeydown = (e: KeyboardEvent) => { if (e.key === 'Escape') closeLightbox(); };

// TOC actif au scroll
let io: IntersectionObserver | null = null;
onMounted(() => {
    const header = document.querySelector('.guide-header') as HTMLElement | null;
    const headerH = (header?.offsetHeight ?? 0) + 10;

    const sections = steps.value
        .map(s => document.getElementById(s.id))
        .filter((el): el is HTMLElement => !!el);

    io = new IntersectionObserver((entries) => {
        // garde la section au plus grand ratio visible
        const best = entries
            .filter(e => e.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (best) current.value = best.target.id;
    }, {
        root: null,
        rootMargin: `-${headerH}px 0px -40% 0px`,
        threshold: Array.from({ length: 10 }, (_, i) => (i + 1) / 10), // 0.1..1
    });

    sections.forEach(s => io!.observe(s));
    window.addEventListener('keydown', onKeydown);
});

onBeforeUnmount(() => {
    if (io) { io.disconnect(); io = null; }
    window.removeEventListener('keydown', onKeydown);
});


onBeforeUnmount(() => {
    if (io) io.disconnect();
    window.removeEventListener('keydown', onKeydown);
});

const nextHref = (id: string) => {
    const idx = steps.value.findIndex(s => s.id === id);
    return idx >= 0 && idx < steps.value.length - 1 ? `#${steps.value[idx + 1].id}` : '#done';
};
const prevHref = (id: string) => {
    const idx = steps.value.findIndex(s => s.id === id);
    return idx > 0 ? `#${steps.value[idx - 1].id}` : `#${steps.value[0].id}`;
};
</script>

<template>
    <Default>
        <div class="guide-root">
            <!-- Header -->
            <header class="guide-header">
                <div class="wrap">
                    <div class="brand"><span class="dot"></span> Topela – Activer le connecteur Pennylane pour la
                        transmission de vos factures électronique via la plateforme agréée</div>
                </div>
            </header>

            <main class="guide-main">
                <!-- TOC -->
                <nav class="toc">
                    <h3>Sommaire</h3>
                    <a v-for="s in steps" :key="s.id" :href="`#${s.id}`" :class="{ active: current === s.id }">
                        {{ s.title }}
                    </a>
                    <a href="#done" class="done-link">Fin : Transmission activée</a>
                </nav>

                <!-- Content -->
                <div class="content">
                    <section v-for="s in steps" :key="s.id" class="card" :id="s.id">
                        <div class="step-head">
                            <h2 class="step-title">{{ s.title }}</h2>
                            <div v-if="s.meta" class="step-meta">{{ s.meta }}</div>
                        </div>
                        <p class="step-text">{{ s.text }}</p>
                        <div class="d-flex justify-content-center">
                        <figure @click="openLightbox(s.img)">
                            <img :src="s.img" :alt="s.alt" :height="500" />
                            <figcaption>{{ s.alt }}</figcaption>
                            <!-- HOTSPOTS -->
                            <button v-for="(t, i) in s.taps" :key="i" class="tap-dot" type="button" :style="{
                                left: t.x + '%',
                                top: t.y + '%'
                            }" :aria-label="t.label || 'Appuyer ici'"
                                @click.stop="t.goNext ? (location.hash = nextHref(s.id)) : openLightbox(s.img)">
                                <span class="pulse" />
                                <span class="dot" />
                                <span v-if="t.label" class="hint">{{ t.label }}</span>
                            </button>
                        </figure>
                        </div>

                        <div class="nextprev">
                            <a class="btn" :href="prevHref(s.id)">← Précédent</a>
                            <a class="btn" :href="nextHref(s.id)">Suivant →</a>
                        </div>
                    </section>

                    <!-- Finale -->
                    <section class="card" :id="finale.id">
                        <div class="step-head">
                            <h2 class="step-title">{{ finale.title }}</h2>
                        </div>
                        <p class="step-text">{{ finale.text }}</p>
                        <ul class="bullets">
                            <li v-for="b in finale.bullets" :key="b">{{ b }}</li>
                        </ul>
                        <div class="nextprev">
                            <a class="btn" :href="`#${steps[0].id}`">↑ Retour au début</a>
                        </div>
                    </section>
                </div>
            </main>

            <!-- Lightbox -->
            <div class="lightbox" :class="{ open: lightboxOpen }" @click.self="closeLightbox">
                <span class="close" aria-label="Fermer" @click="closeLightbox">×</span>
                <img v-if="lightboxSrc" :src="lightboxSrc" alt="Agrandissement" />
            </div>
        </div>
    </Default>
</template>

<style scoped>
@import url('@/assets/css/bootstrap-extended.css');
@import url('@/assets/css/animate.css');
@import url('@/assets/css/main.css');
@import url('@/assets/css/responsive.css');

/* Base */
.guide-root {
    --bg: #ffffff;
    --card: #ffffff;
    --ink: #233648;
    --muted: #9bb0d0;
    --accent: #32A5CF;
    color: var(--ink);
    background: linear-gradient(360deg, #ffffff, #233648);
    min-height: 100vh;
    padding-top: 90px;

}

.wrap {
    max-width: 1100px;
    margin: auto;
    padding: 16px 20px;
}

.brand {
    display: flex;
    align-items: center;
    gap: 12px;
    font-weight: 700;
    letter-spacing: .2px;
}

.brand .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: var(--accent);
    box-shadow: 0 0 16px var(--accent);
}

.guide-header {
    position: sticky;
    top: 90px;
    z-index: 10;
    backdrop-filter: blur(8px);
    background: #ffffff50;
    border-bottom: 1px solid rgba(255, 255, 255, .06);
}

.guide-main {
    display: grid;
    grid-template-columns: 260px 1fr;
    gap: 24px;
    padding: 28px 20px 60px;
    max-width: 1200px;
    margin: auto;
}

.toc {
    position: sticky;
    top: 150px;
    align-self: start;
    background: #ffffff;
    border: 2px solid rgba(8, 8, 8, 0.06);
    box-shadow: 0 8px 24px rgba(0, 0, 0, .12);
    border-radius: 14px;
    padding: 14px;
}

.toc h3 {
    margin: 0 0 8px;
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: .12em;
    color: var(--ink);
}

.toc a {
    display: block;
    padding: 8px 10px;
    border-radius: 10px;
    color: var(--accent) !important;
    text-decoration: none;
}

.toc a:hover {
    background: rgba(50, 165, 207, .15);
}

.toc a.active {
    background: rgba(50, 165, 207, .22);
    border: 1px solid rgba(50, 165, 207, .35);
}

.toc .done-link {
    margin-top: 8px;
    display: block;
    opacity: .85;
}

.card {
    background: #ffffff;
    border: 2px solid rgba(8, 8, 8, 0.06);
    border-radius: 18px;
    padding: 22px;
    margin-bottom: 20px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, .12);
    scroll-margin-top: 110px;
}

.step-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
}

.step-title {
    margin: 0;
    font-size: 20px;
}

.step-meta {
    color: var(--muted);
    font-size: 13px;
}

.step-text {
    margin: 0 0 12px 0;
}

figure {
    position: relative;
    margin: 0;
    background: rgba(255, 255, 255, .03);
    border: 1px solid rgba(255, 255, 255, .06);
    border-radius: 14px;
    padding: 10px;
    cursor: zoom-in;
}

figure img {
    height: 500px;
    border-radius: 10px;
    display: block;
}

figcaption {
    margin-top: 8px;
    font-size: 13px;
    color: var(--muted);
    text-align: center ;
}

.nextprev {
    display: flex;
    gap: 10px;
    margin-top: 14px;
}

.btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, .12);
    color: var(--accent) !important;
    text-decoration: none;
}

.btn:hover {
    border-color: rgba(50, 165, 207, .5);
    background: rgba(50, 165, 207, .15);
}

/* Lightbox */
.lightbox {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, .75);
    display: none;
    align-items: center;
    justify-content: center;
    padding: 24px;
    z-index: 9100;
}

.lightbox.open {
    display: flex;
}

.lightbox img {
    max-width: 95vw;
    max-height: 90vh;
    border-radius: 12px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, .6);
}

.lightbox .close {
    position: fixed;
    top: 16px;
    right: 50px;
    font-size: 28px;
    color: white;
    cursor: pointer;
}

/* Responsive */
@media (max-width:900px) {
    .guide-main {
        grid-template-columns: 1fr;
    }

    .toc {
        position: static;
    }
}

/* Bouton “tap” (point cliquable) */
.tap-dot {
    position: absolute;
    transform: translate(-50%, -50%);
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: transparent;
    border: 0;
    padding: 0;
    cursor: pointer;
    z-index: 2;
    /* au-dessus de l'image */
}

/* Cercle central */
.tap-dot .dot {
    width: 14px;
    height: 14px;
    border-radius: 999px;
    background: var(--accent);
    box-shadow: 0 0 12px rgba(50, 165, 207, .6);
}

/* Onde pulsante */
.tap-dot .pulse {
    position: absolute;
    inset: -10px;
    /* agrandit la zone animée autour du point */
    border-radius: 999px;
    border: 2px solid rgba(50, 165, 207, .55);
    animation: tapPulse 1.6s ease-out infinite;
    pointer-events: none;
}

@keyframes tapPulse {
    0% {
        transform: scale(0.5);
        opacity: .9;
    }

    70% {
        transform: scale(1.4);
        opacity: .15;
    }

    100% {
        transform: scale(1.6);
        opacity: 0;
    }
}

/* Petite étiquette optionnelle */
.tap-dot .hint {
    position: relative;
    top: -1px;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 12px;
    background: #F0A43D;
    color: white;
    border: 1px solid rgba(255, 255, 255, .1);
    white-space: nowrap;
}

.tap-dot:hover .hint {
    filter: brightness(1.05);
}
</style>
